import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Briefcase, MessageSquare, CheckSquare, AlertTriangle, Inbox } from 'lucide-react';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { useNotifications } from '@/hooks/useNotifications';
import { buildDealPath } from '@/shell/routeAccess.js';

function timeAgo(dateStr) {
    if (!dateStr) return '';
    try {
        const d = new Date(dateStr);
        const diff = Date.now() - d.getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return 'just now';
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(hrs / 24);
        if (days === 1) return 'yesterday';
        if (days < 7) return `${days}d ago`;
        return d.toLocaleDateString();
    } catch {
        return '';
    }
}

function typeIcon(type) {
    switch (type) {
        case 'task_assigned': return <CheckSquare className="size-4 text-amber-600" />;
        case 'mention': return <MessageSquare className="size-4 text-indigo-600" />;
        case 'deal_stage_changed': return <Briefcase className="size-4 text-blue-600" />;
        case 'deal_won': return <Briefcase className="size-4 text-emerald-600" />;
        case 'deal_lost': return <AlertTriangle className="size-4 text-red-600" />;
        default: return <Bell className="size-4 text-muted-foreground" />;
    }
}

function notificationHref(notification) {
    const { entity_type, entity_id, meta_data, metadata } = notification;
    const meta = meta_data || metadata || {};
    const entityType = String(entity_type || '').toLowerCase();
    const id = entity_id;
    const dealId = meta.deal_id || meta.dealId || null;

    switch (entityType) {
        case 'deal':
            return buildDealPath(id, { tab: 'timeline' });
        case 'activity':
            return buildDealPath(dealId || id, { tab: 'timeline', highlight: id, filter: 'activity' });
        case 'comment':
            return buildDealPath(dealId, { tab: 'timeline', highlight: id, filter: 'comments' });
        case 'client':
        case 'account':
            return id ? `/clients/${id}` : '/clients';
        case 'contact':
            return id ? `/contacts/${id}` : '/contacts';
        case 'next_step':
            return buildDealPath(dealId, { tab: 'tasks', highlight: id });
        default:
            if (dealId) return buildDealPath(dealId, { tab: 'timeline' });
            if (id && entityType.includes('deal')) return buildDealPath(id, { tab: 'timeline' });
            return '/deals';
    }
}

export default function NotificationBell() {
    const navigate = useNavigate();
    const { notifications, unreadCount, loading, markAsRead, markAllAsRead, ensureListLoaded } = useNotifications();
    const [open, setOpen] = useState(false);

    const handleOpenChange = (next) => {
        setOpen(next);
        if (next) void ensureListLoaded();
    };

    const handleClick = (n, e) => {
        if (!n.is_read) markAsRead(n.id);
        setOpen(false);
        const href = notificationHref(n);
        if (e?.metaKey || e?.ctrlKey || e?.button === 1) {
            window.open(href, '_blank', 'noopener,noreferrer');
            return;
        }
        navigate(href);
        if (e) e.preventDefault();
    };

    const items = useMemo(() => notifications.slice(0, 20), [notifications]);

    return (
        <>
            <button
                onClick={() => handleOpenChange(true)}
                aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
                className="fixed bottom-6 right-6 z-[60] size-12 rounded-full bg-primary text-primary-foreground shadow-lg shadow-black/20 hover:bg-primary/90 active:scale-95 flex items-center justify-center transition-all duration-200 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
                <Bell className="size-5" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 flex items-center justify-center rounded-full bg-red-600 text-white text-[11px] font-bold leading-none ring-2 ring-white">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            <Drawer open={open} onOpenChange={handleOpenChange} direction="right" shouldScaleBackground={false}>
                <DrawerContent
                    unstyled
                    className="activity-drawer notification-drawer"
                    overlayClassName="activity-drawer-overlay active"
                    aria-labelledby="notificationDrawerTitle"
                    style={{ width: '400px', maxWidth: '92vw' }}
                >
                    <header className="activity-drawer-header">
                        <div>
                            <p className="activity-drawer-eyebrow">Notifications</p>
                            <DrawerTitle unstyled id="notificationDrawerTitle" className="text-base font-bold">
                                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                            </DrawerTitle>
                        </div>
                        <div className="flex items-center gap-2">
                            {unreadCount > 0 && (
                                <button
                                    onClick={markAllAsRead}
                                    className="text-xs text-primary hover:underline flex items-center gap-1 px-2 py-1 rounded hover:bg-muted"
                                >
                                    <CheckCheck className="size-3.5" /> Mark all
                                </button>
                            )}
                            <button type="button" className="activity-drawer-close" onClick={() => setOpen(false)} aria-label="Close">×</button>
                        </div>
                    </header>

                    <div className="activity-drawer-body" id="notificationDrawerBody">
                        <div className="activity-drawer-feed">
                            {loading && notifications.length === 0 && <p className="field-hint p-4 text-center">Loading…</p>}
                            {!loading && notifications.length === 0 && (
                                <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                                    <div className="size-12 rounded-full bg-muted flex items-center justify-center mb-3">
                                        <Inbox className="size-6 text-muted-foreground" />
                                    </div>
                                    <p className="text-sm font-medium text-foreground">No notifications</p>
                                    <p className="text-xs text-muted-foreground mt-1">When someone mentions you or assigns a task, you’ll see it here.</p>
                                </div>
                            )}
                            {items.map((n) => {
                                const href = notificationHref(n);
                                return (
                                    <Link
                                        key={n.id}
                                        to={href}
                                        onClick={(e) => handleClick(n, e)}
                                        className={`w-full text-left flex gap-3 px-4 py-3 hover:bg-muted/60 border-b border-border/50 last:border-0 transition-colors ${!n.is_read ? 'bg-primary/[0.04] hover:bg-primary/[0.08]' : ''}`}
                                    >
                                        <div className="shrink-0 mt-0.5">{typeIcon(n.notification_type)}</div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm leading-snug ${!n.is_read ? 'font-semibold text-foreground' : 'font-normal text-foreground/80'}`}>{n.title}</p>
                                            {n.body && <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.body}</p>}
                                            <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2">
                                                <span>{timeAgo(n.created_at)}</span>
                                                {n.entity_type && <span className="px-1.5 py-0.5 rounded bg-muted text-[10px] capitalize">{n.entity_type}</span>}
                                            </p>
                                        </div>
                                        {!n.is_read && <span className="size-2 rounded-full bg-primary shrink-0 mt-2" aria-hidden="true" />}
                                    </Link>
                                );
                            })}
                        </div>
                    </div>

                    <footer className="activity-drawer-footer" style={{ display: 'flex', justifyContent: 'center', padding: '12px' }}>
                        <span className="text-xs text-muted-foreground">Click to view • Swipe or × to close</span>
                    </footer>
                </DrawerContent>
            </Drawer>
        </>
    );
}
